/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { useSysytemOptionsForm } from "./hooks";
import { InputField } from "../../Shared/Radix/Inputs/InputField";
import { TextAreaField } from "../../Shared/Radix/Inputs/TextAreaField";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { useSelector } from "react-redux";
import { errorMsgSelector } from "../../../store/helpers/helperSelectors";
import { setErrorMsg } from "../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../store/hooks";
import { store } from "../../../store";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";

const SystemOptions: React.FC = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const dispatch = useStoreDispatch();
  const storeErrMsg = useSelector(errorMsgSelector);
  const { isLoading, submitHandler, formData, setFormData, cancelHandler, onBlurHandler } = useSysytemOptionsForm();

  const onFieldChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });
    dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, [name]: "" }));
  };

  const onTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, [name]: "" }));
  };

  const handleMaintenanceMode = (checked: boolean) => {
    setFormData({ ...formData, maintenanceModeSystem: checked });
  };

  const handleEnterEditMode = () => {
    setIsEditMode(true);
  };

  const handleCancelEditMode = () => {
    cancelHandler();
    setIsEditMode(false);
  };

  const handleSaveClick = async (event: React.FormEvent) => {
    event.preventDefault();
    const isSuccess = await submitHandler(event);
    if (isSuccess === true || isSuccess === undefined) {
      setIsEditMode(false);
    }
  };

  const renderValue = (value: string | number | boolean | null | undefined) => {
    if (typeof value === "boolean") {
      return value ? "Enabled" : "Disabled";
    }

    if (value === undefined || value === null || value === "") {
        return "—";
    }

    return value;
  };

  const renderReadOnlyField = (
    label: string,
    value: string | number | boolean | null | undefined,
    required = false,
  ) => (
    <div className="flex flex-col gap-1">
      <span className="text-sm text-muted-foreground">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </span>
      <div className="text-sm font-semibold text-foreground">{renderValue(value)}</div>
    </div>
  );

  return (
    <div
      className="flex min-w-0 w-full flex-col overflow-x-hidden overflow-y-auto px-4 pt-3 pb-6 md:px-3"
      style={{ color: "var(--foreground)" }}
    >
      <Card>
        <CardHeader className="flex flex-col gap-6 space-y-0 p-3 pl-6 pr-6 pt-4 pb-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mt-2">
            <div className="space-y-1.5">
              <CardTitle className="mt-0 mb-0">Server settings</CardTitle>
              <CardDescription>
               System identity and maintenance mode configuration
              </CardDescription>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end mt-0">
              {!isEditMode ? (
                <Button type="button" onClick={handleEnterEditMode} className="bg-primary text-primary-foreground hover:bg-primary/90">
                  Edit
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" disabled={isLoading} onClick={handleCancelEditMode}>
                    Cancel
                  </Button>
                  <Button type="submit" form="system-options-form" disabled={isLoading || Object.values(storeErrMsg || {}).some(e => Boolean(e))} className="bg-primary text-primary-foreground shadow hover:bg-primary/80">
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="-mx-6 border-t border-border/50 -mr-6" aria-hidden />
        </CardHeader>
        
        <CardContent className="pt-6">
          <form id="system-options-form" autoComplete="off" className="flex flex-col gap-6" onSubmit={handleSaveClick}>
            <div className="space-y-6">
              
              <div className={`grid min-w-0 grid-cols-1 gap-x-6 ${isEditMode ? 'gap-y-6' : 'gap-y-3'}`}>
                <div className="flex min-w-0 flex-col gap-2">
                  {isEditMode ? (
                    <div className="flex items-center gap-2 pb-2">
                      <Checkbox
                        id="maintenanceModeSystem"
                        checked={!!formData?.maintenanceModeSystem}
                        onCheckedChange={(checked) => handleMaintenanceMode(!!checked)}
                        className="m-0"
                      />
                      <Label htmlFor="maintenanceModeSystem" className="cursor-pointer">
                        Server Maintenance Mode
                      </Label>
                    </div>
                  ) : (
                    renderReadOnlyField("Server Maintenance Mode", !!formData?.maintenanceModeSystem)
                  )}
                </div>
              </div>

              <div className="pt-2">
                <div className={`grid min-w-0 gap-x-6 ${isEditMode ? 'grid-cols-1 gap-y-6' : 'grid-cols-1 md:grid-cols-[360px_minmax(0,1fr)] gap-y-3'}`}>
                  
                  <div className="flex min-w-0 flex-col gap-2">
                    {isEditMode ? (
                      <InputField
                        name="name"
                        label="System Name"
                        value={formData.name || ""}
                        onChange={onFieldChange}
                        onBlur={onBlurHandler}
                        placeholder="Enter system name"
                        controlSize="lg"
                        required
                      />
                    ) : (
                      renderReadOnlyField("System Name", formData.name, true)
                    )}
                  </div>

                   <div className="flex min-w-0 flex-col gap-2">
                    {isEditMode ? (
                      <InputField
                        name="location"
                        label="System Location"
                        value={formData.location || ""}
                        onChange={onFieldChange}
                        onBlur={onBlurHandler}
                        placeholder="Enter system location"
                        controlSize="lg"
                        required
                      />
                    ) : (
                      renderReadOnlyField("System Location", formData.location, true)
                    )}
                  </div>

                  <div className="flex min-w-0 flex-col gap-2">
                    {isEditMode ? (
                      <InputField
                        name="contact"
                        label="System Contact"
                        value={formData.contact || ""}
                        onChange={onFieldChange}
                        placeholder="Enter system contact"
                        controlSize="lg"
                      />
                    ) : (
                      renderReadOnlyField("System Contact", formData.contact)
                    )}
                  </div>

                  <div className={`flex min-w-0 flex-col gap-2 ${isEditMode ? 'col-span-1' : 'md:col-span-2 pt-4'}`}>
                    {isEditMode ? (
                      <TextAreaField
                        name="description"
                        label="System Description"
                        value={formData.description || ""}
                        onChange={onFieldChange}
                        onBlur={onBlurHandler}
                        placeholder="Enter system description"
                        controlSize="full"
                      />
                    ) : (
                      renderReadOnlyField("System Description", formData.description)
                    )}
                  </div>

                </div>
              </div>

            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemOptions;
